import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Drawer, Empty, Modal, Segmented, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, LeftOutlined, RightOutlined, TeamOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { PageLayout } from '../../ui';
import { useGetDriversQuery } from '../../store/api/referencesApi';
import { useGetAllRoutesQuery } from '../../store/api/routesApi';
import { navigateToReferenceCard } from '../../utils/referenceCardNavigation';
import type { Route } from '../../types';

const { Text } = Typography;

const DRIVER_COLUMN_WIDTH = 260;
const DRIVER_SCHEDULE_STORAGE_KEY = 'driverSchedule.selectedDriverIds';

const ACTIVE_ROUTE_STATUSES = new Set(['CREATED', 'ASSIGNED', 'IN_PROGRESS', 'IN_TRANSIT']);
type ScheduleScale = 'week' | 'twoWeeks' | 'month';

const SCALE_META: Record<ScheduleScale, { label: string; days: number | 'month'; dayWidth: number; rowHeight: number; barHeight: number }> = {
  week: { label: 'Неделя', days: 7, dayWidth: 132, rowHeight: 82, barHeight: 54 },
  twoWeeks: { label: '2 недели', days: 14, dayWidth: 92, rowHeight: 72, barHeight: 46 },
  month: { label: 'Месяц', days: 'month', dayWidth: 44, rowHeight: 62, barHeight: 38 },
};

function normalizeRouteStatus(status: unknown): string {
  const value = String(status || '').trim().toLowerCase();
  const map: Record<string, string> = {
    active: 'CREATED',
    new: 'CREATED',
    created: 'CREATED',
    assigned: 'ASSIGNED',
    inprogress: 'IN_TRANSIT',
    'in-progress': 'IN_PROGRESS',
    in_progress: 'IN_PROGRESS',
    in_transit: 'IN_TRANSIT',
  };
  return map[value] || value.toUpperCase();
}

function toDate(value: unknown): Dayjs | null {
  if (!value) return null;
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed.startOf('day') : null;
}

function routeStart(route: Route): Dayjs | null {
  return toDate(route.plannedLoadingDate);
}

function routeEnd(route: Route): Dayjs | null {
  return toDate(route.plannedUnloadingDate);
}

function periodsOverlap(startA: Dayjs, endA: Dayjs, startB: Dayjs, endB: Dayjs): boolean {
  return !startA.isAfter(endB, 'day') && !startB.isAfter(endA, 'day');
}

function routePeriod(route: Route): { start: Dayjs; end: Dayjs } | null {
  const startRaw = routeStart(route);
  const endRaw = routeEnd(route);
  if (!startRaw || !endRaw) return null;
  return startRaw.isAfter(endRaw, 'day')
    ? { start: endRaw, end: startRaw }
    : { start: startRaw, end: endRaw };
}

function routeNumber(route: Route): string {
  return route.number?.trim() || String(route.id);
}

function routeDirection(route: Route): string {
  const from = route.loadingCity?.trim() || route.loadingAddress?.trim() || '—';
  const to = route.unloadingCity?.trim() || route.unloadingAddress?.trim() || '—';
  return `${from} → ${to}`;
}

function statusColor(status: unknown): string {
  const normalized = normalizeRouteStatus(status);
  if (normalized === 'CREATED') return 'default';
  if (normalized === 'ASSIGNED') return 'blue';
  if (normalized === 'IN_PROGRESS' || normalized === 'IN_TRANSIT') return 'processing';
  if (normalized === 'COMPLETED') return 'success';
  if (normalized === 'PROBLEM') return 'error';
  if (normalized === 'CANCELLED') return 'error';
  return 'default';
}

function routeBarColors(status: unknown, hasConflict: boolean): { background: string; border: string; color: string } {
  if (hasConflict || normalizeRouteStatus(status) === 'PROBLEM') {
    return { background: '#fff1f0', border: '#ff4d4f', color: '#a8071a' };
  }
  const normalized = normalizeRouteStatus(status);
  if (normalized === 'COMPLETED') {
    return { background: '#f6ffed', border: '#95de64', color: '#237804' };
  }
  if (normalized === 'IN_PROGRESS' || normalized === 'IN_TRANSIT') {
    return { background: '#e6f4ff', border: '#91caff', color: '#0958d9' };
  }
  return { background: '#f5f5f5', border: '#d9d9d9', color: '#595959' };
}

function routeHasConflict(route: Route, routes: Route[]): boolean {
  const routePeriodValue = routePeriod(route);
  if (!routePeriodValue) return false;
  return routes.some((other) => {
    if (other.id === route.id) return false;
    if (Number(other.driverId) !== Number(route.driverId)) return false;
    if (!ACTIVE_ROUTE_STATUSES.has(normalizeRouteStatus(other.status))) return false;
    const otherPeriod = routePeriod(other);
    if (!otherPeriod) return false;
    return periodsOverlap(routePeriodValue.start, routePeriodValue.end, otherPeriod.start, otherPeriod.end);
  });
}

function cargoTotals(route: Route): { names: string; weight: string; volume: string; places: string } {
  const cargo = Array.isArray(route.cargo) ? route.cargo : [];
  let weight = 0;
  let volume = 0;
  let places = 0;
  const names: string[] = [];

  for (const item of cargo as any[]) {
    const source = item?.cargo || item;
    const name = String(source?.name || '').trim();
    if (name) names.push(name);
    weight += Number(source?.totalWeightKg ?? source?.totalWeight ?? 0) || 0;
    volume += Number(source?.volumeM3 ?? source?.totalVolume ?? 0) || 0;
    places += Number(source?.quantity ?? item?.quantity ?? 0) || 0;
  }

  return {
    names: names.length > 0 ? Array.from(new Set(names)).join(', ') : '—',
    weight: weight > 0 ? `${weight.toLocaleString('ru-RU')} кг` : '—',
    volume: volume > 0 ? `${volume.toLocaleString('ru-RU')} м³` : '—',
    places: places > 0 ? `${places.toLocaleString('ru-RU')}` : '—',
  };
}

function formatDate(value: unknown): string {
  const parsed = toDate(value);
  return parsed ? parsed.format('DD.MM.YYYY') : '—';
}

function startOfWeek(date: Dayjs): Dayjs {
  return date.subtract((date.day() + 6) % 7, 'day').startOf('day');
}

function periodStartForScale(date: Dayjs, scale: ScheduleScale): Dayjs {
  if (scale === 'month') return date.startOf('month');
  return startOfWeek(date);
}

function buildDays(startDate: Dayjs, scale: ScheduleScale): Dayjs[] {
  const scaleDays = SCALE_META[scale].days;
  const daysCount: number = scaleDays === 'month' ? startDate.daysInMonth() : scaleDays;
  return Array.from({ length: daysCount }, (_, index) => startDate.add(index, 'day'));
}

function periodTitle(startDate: Dayjs, endDate: Dayjs, scale: ScheduleScale): string {
  if (scale === 'month') {
    return startDate.toDate().toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  }
  return `${startDate.format('DD.MM.YYYY')} – ${endDate.format('DD.MM.YYYY')}`;
}

function shiftPeriod(date: Dayjs, scale: ScheduleScale, direction: -1 | 1): Dayjs {
  if (scale === 'month') return date.add(direction, 'month').startOf('month');
  return date.add(direction * (scale === 'week' ? 7 : 14), 'day');
}

function isWeekend(day: Dayjs): boolean {
  return day.day() === 0 || day.day() === 6;
}

function weekdayRu(day: Dayjs): string {
  return ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][day.day()];
}

function readStoredDriverIds(): number[] | null {
  try {
    const raw = localStorage.getItem(DRIVER_SCHEDULE_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((value) => Number(value)).filter(Number.isFinite);
  } catch {
    return null;
  }
}

const DriverSchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([]);
  const [activeRoute, setActiveRoute] = useState<Route | null>(null);
  const [driversModalOpen, setDriversModalOpen] = useState(false);
  const [scale, setScale] = useState<ScheduleScale>('month');
  const [periodAnchor, setPeriodAnchor] = useState(() => dayjs().startOf('month'));
  const initializedDriversRef = useRef(false);
  const startDate = useMemo(() => periodStartForScale(periodAnchor, scale), [periodAnchor, scale]);
  const days = useMemo(() => buildDays(startDate, scale), [startDate, scale]);
  const endDate = days[days.length - 1];
  const scaleMeta = SCALE_META[scale];

  const { data: drivers = [], isLoading: driversLoading } = useGetDriversQuery();
  const { data: routesData, isLoading: routesLoading } = useGetAllRoutesQuery({ page: 1, limit: 1000 });

  const companyDrivers = useMemo(
    () => drivers.filter((driver) => driver.isCompanyDriver).sort((a, b) => a.fullName.localeCompare(b.fullName, 'ru')),
    [drivers]
  );

  useEffect(() => {
    if (initializedDriversRef.current || companyDrivers.length === 0) return;
    initializedDriversRef.current = true;
    const storedIds = readStoredDriverIds();
    const companyDriverIds = new Set(companyDrivers.map((driver) => driver.id));
    if (storedIds) {
      setSelectedDriverIds(storedIds.filter((id) => companyDriverIds.has(id)));
      return;
    }
    setSelectedDriverIds(companyDrivers.map((driver) => driver.id));
  }, [companyDrivers]);

  useEffect(() => {
    if (!initializedDriversRef.current) return;
    localStorage.setItem(DRIVER_SCHEDULE_STORAGE_KEY, JSON.stringify(selectedDriverIds));
  }, [selectedDriverIds]);

  const selectedDrivers = useMemo(
    () => companyDrivers.filter((driver) => selectedDriverIds.includes(driver.id)),
    [companyDrivers, selectedDriverIds]
  );

  const routesByDriver = useMemo(() => {
    const map = new Map<number, Route[]>();
    const routes = routesData?.routes ?? [];
    for (const route of routes) {
      if (!route.driverId || !selectedDriverIds.includes(Number(route.driverId))) continue;
      const period = routePeriod(route);
      if (!period) continue;
      if (!periodsOverlap(period.start, period.end, startDate, endDate)) continue;
      const driverRoutes = map.get(Number(route.driverId)) ?? [];
      driverRoutes.push(route);
      map.set(Number(route.driverId), driverRoutes);
    }
    Array.from(map.values()).forEach((driverRoutes: Route[]) => {
      driverRoutes.sort((a: Route, b: Route) => (routeStart(a)?.valueOf() ?? 0) - (routeStart(b)?.valueOf() ?? 0));
    });
    return map;
  }, [routesData?.routes, selectedDriverIds, startDate, endDate]);

  const visibleRoutes = useMemo(
    () => Array.from(routesByDriver.values()).flat(),
    [routesByDriver]
  );

  const loading = driversLoading || routesLoading;
  const timelineWidth = days.length * scaleMeta.dayWidth;
  const isMonthScale = scale === 'month';

  const renderRouteBar = (route: Route) => {
    const period = routePeriod(route);
    if (!period) return null;
    const visibleStart = period.start.isBefore(startDate, 'day') ? startDate : period.start;
    const visibleEnd = period.end.isAfter(endDate, 'day') ? endDate : period.end;
    const startOffset = Math.max(0, visibleStart.diff(startDate, 'day'));
    const duration = Math.max(1, visibleEnd.diff(visibleStart, 'day') + 1);
    const hasConflict = routeHasConflict(route, visibleRoutes);
    const routeBarWidth = duration * scaleMeta.dayWidth - (isMonthScale ? 4 : 12);
    const showDirection = !isMonthScale || routeBarWidth >= 120;
    const colors = routeBarColors(route.status, hasConflict);
    const direction = routeDirection(route);
    const titleText = `№${routeNumber(route)} ${direction}`;

    return (
      <div
        key={route.id}
        role="button"
        tabIndex={0}
        onClick={() => setActiveRoute(route)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') setActiveRoute(route);
        }}
        style={{
          position: 'absolute',
          left: startOffset * scaleMeta.dayWidth + (isMonthScale ? 2 : 6),
          top: isMonthScale ? 12 : 10,
          width: routeBarWidth,
          minWidth: isMonthScale ? 34 : 96,
          height: scaleMeta.barHeight,
          borderRadius: isMonthScale ? 6 : 8,
          padding: isMonthScale ? '3px 5px' : '4px 8px',
          background: colors.background,
          border: `1px solid ${colors.border}`,
          color: colors.color,
          cursor: 'pointer',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tooltip title={titleText}>
          <Space direction="vertical" size={0} style={{ maxWidth: '100%', minWidth: 0, lineHeight: 1.15 }}>
            <Link
              to={`/routes/${route.id}`}
              onClick={(event) => event.stopPropagation()}
              style={{
                fontWeight: 700,
                color: 'inherit',
                fontSize: isMonthScale ? 11 : 13,
                lineHeight: 1.15,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
            >
              №{routeNumber(route)}
            </Link>
            {showDirection ? (
              <Text ellipsis style={{ color: 'inherit', maxWidth: '100%', fontSize: isMonthScale ? 10 : 12, lineHeight: 1.15 }}>
                {direction}
              </Text>
            ) : null}
          </Space>
        </Tooltip>
      </div>
    );
  };

  const activeCargo = activeRoute ? cargoTotals(activeRoute) : null;

  return (
    <PageLayout title="График водителей" titleLevel={2}>
      <Card style={{ marginBottom: 12 }} styles={{ body: { padding: '12px 16px' } }}>
        <Space wrap align="center" size={12} style={{ width: '100%' }}>
          <Space align="center" size={8}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => setPeriodAnchor((prev) => shiftPeriod(prev, scale, -1))}
            />
            <Text strong style={{ minWidth: 220, textAlign: 'center', display: 'inline-block', textTransform: 'capitalize' }}>
              {periodTitle(startDate, endDate, scale)}
            </Text>
            <Button
              icon={<RightOutlined />}
              onClick={() => setPeriodAnchor((prev) => shiftPeriod(prev, scale, 1))}
            />
          </Space>
          <Segmented
            value={scale}
            options={[
              { label: 'Неделя', value: 'week' },
              { label: '2 недели', value: 'twoWeeks' },
              { label: 'Месяц', value: 'month' },
            ]}
            onChange={(value) => {
              const nextScale = value as ScheduleScale;
              setScale(nextScale);
              setPeriodAnchor((prev) => periodStartForScale(prev, nextScale));
            }}
          />
          <Button icon={<TeamOutlined />} onClick={() => setDriversModalOpen(true)}>
            Добавить водителя
          </Button>
        </Space>
      </Card>

      <Card styles={{ body: { padding: 0 } }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : selectedDrivers.length === 0 ? (
          <Empty description="Выберите водителей для отображения" style={{ padding: 48 }} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: DRIVER_COLUMN_WIDTH + timelineWidth }}>
              <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
                <div
                  style={{
                    width: DRIVER_COLUMN_WIDTH,
                    flex: `0 0 ${DRIVER_COLUMN_WIDTH}px`,
                    padding: '12px 16px',
                    borderRight: '1px solid #f0f0f0',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: 600,
                  }}
                >
                  Водитель
                </div>
                {days.map((day) => {
                  const isToday = day.isSame(dayjs(), 'day');
                  const weekend = isWeekend(day);
                  return (
                  <div
                    key={day.toISOString()}
                    style={{
                      width: scaleMeta.dayWidth,
                      flex: `0 0 ${scaleMeta.dayWidth}px`,
                      padding: isMonthScale ? '6px 4px' : '8px 10px',
                      borderRight: '1px solid #f5f5f5',
                      borderBottom: '1px solid #f0f0f0',
                      background: isToday ? '#fff1b8' : weekend ? '#fafafa' : '#fff',
                      boxShadow: isToday ? 'inset 0 -2px 0 #faad14' : undefined,
                      textAlign: isMonthScale ? 'center' : 'left',
                    }}
                  >
                    <Text strong style={{ fontSize: isMonthScale ? 12 : 14 }}>
                      {isMonthScale ? day.format('D') : day.format('DD.MM')}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: isMonthScale ? 10 : 12 }}>
                      {weekdayRu(day)}
                    </Text>
                  </div>
                  );
                })}
              </div>

              {selectedDrivers.map((driver, rowIndex) => {
                const driverRoutes = routesByDriver.get(driver.id) ?? [];
                const rowBackground = rowIndex % 2 === 0 ? '#fff' : '#fafafa';
                return (
                  <div key={driver.id} style={{ display: 'flex', minHeight: scaleMeta.rowHeight, background: rowBackground }}>
                    <div
                      style={{
                        width: DRIVER_COLUMN_WIDTH,
                        flex: `0 0 ${DRIVER_COLUMN_WIDTH}px`,
                        padding: isMonthScale ? '8px 12px' : '12px 16px',
                        borderRight: '1px solid #f0f0f0',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <Button
                        type="link"
                        style={{ fontWeight: 600, padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}
                        onClick={() => navigateToReferenceCard(navigate, `/drivers/${driver.id}`, location)}
                      >
                        {driver.fullName}
                      </Button>
                      <Button
                        size="small"
                        type="text"
                        icon={<CloseOutlined />}
                        title="Убрать из списка"
                        onClick={() => setSelectedDriverIds((prev) => prev.filter((id) => id !== driver.id))}
                      />
                    </div>
                    <div
                      style={{
                        position: 'relative',
                        width: timelineWidth,
                        flex: `0 0 ${timelineWidth}px`,
                        minHeight: scaleMeta.rowHeight,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ display: 'flex', position: 'absolute', inset: 0 }}>
                        {days.map((day) => {
                          const isToday = day.isSame(dayjs(), 'day');
                          const weekend = isWeekend(day);
                          return (
                            <div
                              key={day.toISOString()}
                              style={{
                                width: scaleMeta.dayWidth,
                                flex: `0 0 ${scaleMeta.dayWidth}px`,
                                borderRight: '1px solid #f5f5f5',
                                background: isToday ? 'rgba(250, 173, 20, 0.16)' : weekend ? 'rgba(0, 0, 0, 0.025)' : rowBackground,
                              }}
                            />
                          );
                        })}
                      </div>
                      {driverRoutes.map(renderRouteBar)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Modal
        title="Добавить водителя"
        open={driversModalOpen}
        onCancel={() => setDriversModalOpen(false)}
        onOk={() => setDriversModalOpen(false)}
        okText="Готово"
        cancelText="Закрыть"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space>
            <Button size="small" onClick={() => setSelectedDriverIds(companyDrivers.map((driver) => driver.id))}>
              Выбрать всех
            </Button>
            <Button size="small" onClick={() => setSelectedDriverIds([])}>
              Очистить
            </Button>
          </Space>
          <Checkbox.Group
            value={selectedDriverIds}
            onChange={(values) => setSelectedDriverIds(values.map((value) => Number(value)).filter(Number.isFinite))}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {companyDrivers.map((driver) => (
                <Checkbox key={driver.id} value={driver.id}>
                  {driver.fullName}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Space>
      </Modal>

      <Drawer
        title={activeRoute ? `Маршрут №${routeNumber(activeRoute)}` : 'Маршрут'}
        open={!!activeRoute}
        width={420}
        onClose={() => setActiveRoute(null)}
        extra={
          activeRoute ? (
            <Button type="primary" onClick={() => navigate(`/routes/${activeRoute.id}`)}>
              Подробнее
            </Button>
          ) : null
        }
      >
        {activeRoute ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Tag color={statusColor(activeRoute.status)}>{normalizeRouteStatus(activeRoute.status)}</Tag>
            </div>
            <Card size="small" title="Даты">
              <Space direction="vertical">
                <Text>Погрузка: {formatDate(activeRoute.plannedLoadingDate)}</Text>
                <Text>Выгрузка: {formatDate(activeRoute.plannedUnloadingDate)}</Text>
              </Space>
            </Card>
            <Card size="small" title="Контрагенты">
              <Space direction="vertical">
                <Text>Клиент: {activeRoute.client?.name || '—'}</Text>
                <Text>Перевозчик: {activeRoute.carrier?.name || 'Свой'}</Text>
              </Space>
            </Card>
            <Card size="small" title="Исполнение">
              <Space direction="vertical">
                <Text>Водитель: {activeRoute.driver?.fullName || '—'}</Text>
                <Text>
                  ТС:{' '}
                  {[activeRoute.vehicle?.brand, activeRoute.vehicle?.registrationNumber].filter(Boolean).join(' ') || '—'}
                </Text>
                <Text>
                  Прицеп:{' '}
                  {[
                    activeRoute.trailerVehicle?.brand,
                    activeRoute.trailerVehicle?.registrationNumber
                  ].filter(Boolean).join(' ') || '—'}
                </Text>
              </Space>
            </Card>
            <Card size="small" title="Адреса">
              <Space direction="vertical">
                <Text>Погрузка: {activeRoute.loadingAddress || '—'}</Text>
                <Text>Выгрузка: {activeRoute.unloadingAddress || '—'}</Text>
              </Space>
            </Card>
            <Card size="small" title="Груз">
              <Space direction="vertical">
                <Text>Наименование: {activeCargo?.names}</Text>
                <Text>Вес: {activeCargo?.weight}</Text>
                <Text>Объем: {activeCargo?.volume}</Text>
                <Text>Места: {activeCargo?.places}</Text>
              </Space>
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </PageLayout>
  );
};

export default DriverSchedulePage;
